import { Building2, BookOpen, Library, GraduationCap, School } from 'lucide-react';

/**
 * Bookseller Branding Configuration
 *
 * This file provides visual branding for bookseller integrations.
 * Currently uses placeholder icons and brand colors.
 *
 * TODO: Replace with actual bookseller logos when available
 * - Add logo image files to /public/logos/booksellers/
 * - Update logoUrl fields to point to actual images
 * - Ensure proper licensing for logo usage
 */

export interface BooksellerBranding {
  slug: string;
  name: string;
  displayName: string;
  logoUrl?: string; // Path to actual logo file (future)
  icon: React.ComponentType<{ className?: string }>; // Lucide icon placeholder
  primaryColor: string; // Brand primary color (hex)
  secondaryColor: string; // Brand secondary color (hex) — used as gradient end
}

/**
 * Major bookseller brands with placeholder branding
 */
export const booksellerBranding: Record<string, BooksellerBranding> = {
  vitalsource: {
    slug: 'vitalsource',
    name: 'VitalSource',
    displayName: 'VitalSource Bookshelf',
    icon: BookOpen,
    primaryColor: '#0066CC', // VitalSource blue (approximate)
    secondaryColor: '#004C99',
  },
  redshelf: {
    slug: 'redshelf',
    name: 'RedShelf',
    displayName: 'RedShelf eReader',
    icon: Library,
    primaryColor: '#DC2626', // RedShelf red (approximate)
    secondaryColor: '#991B1B',
  },
  cengage: {
    slug: 'cengage',
    name: 'Cengage',
    displayName: 'Cengage Unlimited',
    icon: GraduationCap,
    primaryColor: '#EA580C', // Cengage orange (approximate)
    secondaryColor: '#C2410C',
  },
  mcgrawhill: {
    slug: 'mcgrawhill',
    name: 'McGraw Hill',
    displayName: 'McGraw Hill Connect',
    icon: School,
    primaryColor: '#0891B2', // McGraw Hill teal (approximate)
    secondaryColor: '#0E7490',
  },
  pearson: {
    slug: 'pearson',
    name: 'Pearson',
    displayName: 'Pearson+',
    icon: BookOpen,
    primaryColor: '#7C3AED', // Pearson purple (approximate)
    secondaryColor: '#6D28D9',
  },
  // Generic fallback for unknown booksellers
  default: {
    slug: 'default',
    name: 'Bookseller',
    displayName: 'Digital Bookseller',
    icon: Building2,
    primaryColor: '#6B7280',
    secondaryColor: '#4B5563',
  },
};

/**
 * Get branding configuration for a bookseller
 * Falls back to default branding if not found
 */
export function getBooksellerBranding(slug: string): BooksellerBranding {
  const normalizedSlug = slug.toLowerCase().trim();
  return booksellerBranding[normalizedSlug] || booksellerBranding.default;
}

/**
 * BooksellerLogo Component
 * Renders bookseller logo with fallback to icon
 */
interface BooksellerLogoProps {
  slug: string;
  size?: 'sm' | 'md' | 'lg';
  showName?: boolean;
  className?: string;
}

export function BooksellerLogo({
  slug,
  size = 'md',
  showName = false,
  className = '',
}: BooksellerLogoProps) {
  const branding = getBooksellerBranding(slug);
  const Icon = branding.icon;

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Logo Container */}
      <div
        className={`${sizeClasses[size]} rounded-lg flex items-center justify-center flex-shrink-0`}
        style={{
          backgroundImage: `linear-gradient(to bottom right, ${branding.primaryColor}, ${branding.secondaryColor})`,
        }}
      >
        {branding.logoUrl ? (
          // Real logo (when available)
          <img
            src={branding.logoUrl}
            alt={`${branding.name} logo`}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          // Icon placeholder
          <Icon className={`${iconSizeClasses[size]} text-white`} />
        )}
      </div>

      {/* Name */}
      {showName && (
        <div>
          <div className="font-semibold text-neutral-900">{branding.name}</div>
          {branding.displayName !== branding.name && (
            <div className="text-sm text-neutral-600">{branding.displayName}</div>
          )}
        </div>
      )}
    </div>
  );
}
